import { isNil, partial } from 'lodash';
import {
  atomFamily,
  CallbackInterface,
  DefaultValue,
  RecoilState,
  RecoilValue,
  selectorFamily,
  SerializableParam,
  useRecoilCallback,
} from 'recoil';

// TODO: Support for disordered arguments between callback args and factory method args. For now, allowing only function
//  overloads with ordered args only (commenting out overloads with prefixed `undefined` args), and applied factory as `partial`.

// TODO: Unioned return type.
export type RecoilValueFamily<TValue = unknown, TParam extends SerializableParam = SerializableParam> = ReturnType<
  typeof selectorFamily<TValue, TParam> | typeof atomFamily<TValue, TParam>
>;
export type RecoilStateFamily<TValue = unknown, TParam extends SerializableParam = SerializableParam> = ReturnType<
  typeof atomFamily<TValue, TParam>
>;

export type RecoilValueReference<
  TKey extends string = string,
  TValue = unknown,
  TParam extends SerializableParam = string
> =
  | {
      key: TKey;
      state: RecoilValue<TValue>;
      params?: never;
    }
  | {
      key: TKey;
      state: RecoilValueFamily<TValue, TParam>;
      params: TParam[];
    };

export type RecoilStateReference<
  TKey extends string = string,
  TValue = any,
  TParam extends SerializableParam = string
> = Pick<RecoilValueReference<TKey, TValue, TParam>, 'key'> &
  (
    | {
        state: RecoilState<TValue>;
        params?: never;
      }
    | {
        state: RecoilStateFamily<TValue, TParam>;
        params: TParam[];
      }
  );

export type RecoilValuesRecord<TKey extends string = string> = {
  [key in TKey]: ReturnType<typeof getValue | typeof getValueFamily>;
};

// TODO: Generics for returned function.
export type RecoilCallback<TArgs extends readonly unknown[], TReturn> = Parameters<
  typeof useRecoilCallback<TArgs, TReturn>
>[0]; // (cbInterface: CallbackInterface) => <CBArgs extends readonly unknown[], CBReturn>(...args: CBArgs) => CBReturn;
export type RecoilAsyncCallback<TArgs extends readonly unknown[], TReturn> = RecoilCallback<TArgs, Promise<TReturn>>;

// TODO: Are these still needed (or at least useful)? Or have the become extraneous wrappers?
export type GetValueCallback<TArgs extends readonly unknown[] = [], TValue = unknown> = RecoilCallback<TArgs, TValue>;
export type GetValueAsyncCallback<TArgs extends readonly unknown[] = [], TValue = unknown> = RecoilAsyncCallback<
  TArgs,
  TValue
>;
export type SetValueCallback<TValue = unknown, TArgs extends readonly unknown[] = [newValue: TValue]> = RecoilCallback<
  TArgs,
  void
>;
export type SetValueAsyncCallback<
  TValue = unknown,
  TArgs extends readonly unknown[] = [newValue: TValue]
> = RecoilAsyncCallback<TArgs, void>;

export const getValue = <TValue>(snapshot: CallbackInterface['snapshot'], state: RecoilValue<TValue>) =>
  snapshot.getLoadable(state).valueMaybe();
export function getValueCallback<TValue>(): GetValueCallback<[state: RecoilValue<TValue>], TValue | undefined>;
export function getValueCallback<TValue>(state: RecoilValue<TValue>): GetValueCallback<[], TValue | undefined>;
export function getValueCallback<CBValue>(cbState?: RecoilValue<CBValue>): ReturnType<typeof getValueCallback> {
  return ({ snapshot }: CallbackInterface) =>
    <TValue extends CBValue>(state: RecoilValue<TValue> = cbState as RecoilValue<TValue>) =>
      getValue(snapshot, state);
}

export const getValueAsync = async <TValue>(snapshot: CallbackInterface['snapshot'], state: RecoilValue<TValue>) =>
  await snapshot.getPromise(state);
export function getValueAsyncCallback<TValue>(): GetValueAsyncCallback<[state: RecoilValue<TValue>], TValue>;
export function getValueAsyncCallback<TValue>(state: RecoilValue<TValue>): GetValueAsyncCallback<[], TValue>;
export function getValueAsyncCallback<CBValue>(
  cbState?: RecoilValue<CBValue>
): ReturnType<typeof getValueAsyncCallback> {
  return ({ snapshot }: CallbackInterface) =>
    async <TValue extends CBValue>(state: RecoilValue<TValue> = cbState as RecoilValue<TValue>) =>
      await getValueAsync(snapshot, state);
}

export const getValueFamily = <TValue, TParam extends string>(
  snapshot: CallbackInterface['snapshot'],
  state: RecoilValueFamily<TValue, TParam>,
  params: TParam[]
) =>
  params.reduce((acc, curr) => {
    acc[curr] = getValue(snapshot, state(curr));
    return acc;
  }, {} as Record<TParam, TValue | undefined>);
export function getValueFamilyCallback<TValue, TParam extends string>(): GetValueCallback<
  [state: RecoilValueFamily<TValue, TParam>, params: TParam[]],
  Record<TParam, TValue | undefined>
>;
export function getValueFamilyCallback<TValue, TParam extends string>(
  state: RecoilValueFamily<TValue, TParam>
): GetValueCallback<[params: TParam[]], Record<TParam, TValue | undefined>>;
// export function getValueFamilyCallback<TValue, TParam extends string>(state: undefined, params: TParam[]): GetValueCallback<[state: RecoilValueFamily<TValue, TParam>], Record<TParam, TValue | undefined>>;
export function getValueFamilyCallback<TValue, TParam extends string>(
  state: RecoilValueFamily<TValue, TParam>,
  params: TParam[]
): GetValueCallback<[], Record<TParam, TValue | undefined>>;
export function getValueFamilyCallback<CBValue, CBParam extends string>(
  ...cbArgs: [cbState?: RecoilValueFamily<CBValue, CBParam>, cbParams?: CBParam[]]
): ReturnType<typeof getValueFamilyCallback> {
  const [cbState, cbParams] = cbArgs;
  return ({ snapshot }: CallbackInterface) =>
    partial(
      <TValue extends CBValue, TParam extends CBParam>(
        state: RecoilValueFamily<TValue, TParam> = cbState as RecoilValueFamily<TValue & CBValue, TParam>,
        params: TParam[] = cbParams as TParam[]
      ) => getValueFamily(snapshot, state, params),
      ...cbArgs
    );
}

export const getValueFamilyAsync = async <TValue, TParam extends string>(
  snapshot: CallbackInterface['snapshot'],
  state: RecoilValueFamily<TValue, TParam>,
  params: TParam[]
) => {
  // TODO: Confirm this runs fully asynchronously (i.e. `.map` doesn't wait in between each `await`).
  const values = await Promise.all(
    params.map(async key => ({ key, value: await getValueAsync(snapshot, state(key)) }))
  );
  return values.reduce((acc, { key, value }) => {
    acc[key] = value;
    return acc;
  }, {} as Record<TParam, TValue>);
};
export function getValueFamilyAsyncCallback<TValue, TParam extends string>(): GetValueAsyncCallback<
  [state: RecoilValueFamily<TValue, TParam>, params: TParam[]],
  Record<TParam, TValue>
>;
export function getValueFamilyAsyncCallback<TValue, TParam extends string>(
  state: RecoilValueFamily<TValue, TParam>
): GetValueAsyncCallback<[params: TParam[]], Record<TParam, TValue>>;
// export function getValueFamilyAsyncCallback<TValue, TParam extends string>(state: undefined, params: TParam[]): GetValueAsyncCallback<[state: RecoilValueFamily<TValue, TParam>], Record<TParam, TValue>>;
export function getValueFamilyAsyncCallback<TValue, TParam extends string>(
  state: RecoilValueFamily<TValue, TParam>,
  params: TParam[]
): GetValueAsyncCallback<[], Record<TParam, TValue>>;
export function getValueFamilyAsyncCallback<CBValue, CBParam extends string>(
  ...cbArgs: [cbState?: RecoilValueFamily<CBValue, CBParam>, cbParams?: CBParam[]]
): ReturnType<typeof getValueFamilyAsyncCallback> {
  const [cbState, cbParams] = cbArgs;
  return ({ snapshot }: CallbackInterface) =>
    partial(
      async <TValue extends CBValue, TParam extends CBParam>(
        state: RecoilValueFamily<TValue, TParam> = cbState as RecoilValueFamily<TValue & CBValue, TParam>,
        params: TParam[] = cbParams as TParam[]
      ) => await getValueFamilyAsync(snapshot, state, params),
      ...cbArgs
    );
}

export const getValues = <TValues extends RecoilValuesRecord>(
  snapshot: CallbackInterface['snapshot'],
  states: RecoilValueReference[]
) =>
  states.reduce((acc, { key, state, params }) => {
    if (!isNil(params)) {
      acc[key] = getValueFamily(snapshot, state as RecoilValueFamily<unknown, string>, params);
    } else {
      acc[key] = getValue(snapshot, state as RecoilValue<unknown>);
    }
    return acc;
  }, {} as RecoilValuesRecord) as TValues;
export function getValuesCallback<TValues extends RecoilValuesRecord>(): GetValueCallback<
  [states: RecoilValueReference[]],
  TValues
>;
export function getValuesCallback<TValues extends RecoilValuesRecord>(
  states: RecoilValueReference[]
): GetValueCallback<[], TValues>;
export function getValuesCallback<CBValues extends RecoilValuesRecord>(
  cbStates?: RecoilValueReference[]
): ReturnType<typeof getValuesCallback> {
  return ({ snapshot }: CallbackInterface) =>
    <TValues extends CBValues>(states: RecoilValueReference[] = cbStates!) =>
      getValues<TValues>(snapshot, states);
}

export const getValuesAsync = async <TValues extends RecoilValuesRecord>(
  snapshot: CallbackInterface['snapshot'],
  states: RecoilValueReference[]
) => {
  const values = await Promise.all(
    states.map(async ({ key, state, params }) => ({
      key,
      value: await (!isNil(params)
        ? getValueFamilyAsync(snapshot, state as RecoilValueFamily<unknown, string>, params)
        : getValueAsync(snapshot, state as RecoilValue<unknown>)),
    }))
  );
  return values.reduce((acc, { key, value }) => {
    acc[key] = value;
    return acc;
  }, {} as RecoilValuesRecord) as TValues;
};
export function getValuesAsyncCallback<TValues extends RecoilValuesRecord>(): GetValueAsyncCallback<
  [states: RecoilValueReference[]],
  TValues
>;
export function getValuesAsyncCallback<TValues extends RecoilValuesRecord>(
  states: RecoilValueReference[]
): GetValueAsyncCallback<[], TValues>;
export function getValuesAsyncCallback<CBValues extends RecoilValuesRecord>(
  cbStates?: RecoilValueReference[]
): ReturnType<typeof getValuesAsyncCallback> {
  return ({ snapshot }: CallbackInterface) =>
    async <TValues extends CBValues>(states: RecoilValueReference[] = cbStates!) =>
      await getValuesAsync<TValues>(snapshot, states);
}

// TODO: Change `newValue` to support `valOrUpdater`.
export const setValue = <TValue>(cbInterface: CallbackInterface, state: RecoilState<TValue>, newValue: TValue) =>
  newValue instanceof DefaultValue ? cbInterface.reset(state) : cbInterface.set(state, newValue);
export function setValueCallback(): SetValueCallback<unknown, [state: RecoilState<any>, newValue: unknown]>;
export function setValueCallback<TValue>(state: RecoilState<TValue>): SetValueCallback<TValue, [newValue: TValue]>;
// export function setValueCallback<TValue>(state: undefined, newValue: TValue): SetValueCallback<TValue, [state: RecoilState<any>]>;
export function setValueCallback<TValue>(state: RecoilState<TValue>, newValue: TValue): SetValueCallback<TValue, []>;
export function setValueCallback<CBValue>(
  ...cbArgs: [cbState?: RecoilState<CBValue>, cbNewValue?: CBValue]
): ReturnType<typeof setValueCallback> {
  const [cbState, cbNewValue] = cbArgs;
  return (cbInterface: CallbackInterface) =>
    partial(
      <TValue extends CBValue>(
        state: RecoilState<TValue> = cbState as RecoilState<TValue & CBValue>,
        newValue: TValue = cbNewValue as TValue
      ) => setValue(cbInterface, state, newValue),
      ...cbArgs
    );
}

// TODO: Change `newValue` to support `valOrUpdater`.
export const setValueAsync = async <TValue>(
  cbInterface: CallbackInterface,
  state: RecoilState<TValue>,
  newValue: TValue
) =>
  await new Promise<void>(resolve => {
    if (newValue instanceof DefaultValue) {
      cbInterface.reset(state);
      resolve();
    } else {
      cbInterface.set<TValue>(state, _prev => {
        resolve();
        return newValue;
      });
    }
  });
export function setValueAsyncCallback(): SetValueAsyncCallback<unknown, [state: RecoilState<any>, newValue: unknown]>;
export function setValueAsyncCallback<TValue>(
  state: RecoilState<TValue>
): SetValueAsyncCallback<TValue, [newValue: TValue]>;
// export function setValueAsyncCallback<TValue>(state: undefined, newValue: TValue): SetValueAsyncCallback<TValue, [state: RecoilState<any>]>;
export function setValueAsyncCallback<TValue>(
  state: RecoilState<TValue>,
  newValue: TValue
): SetValueAsyncCallback<TValue, []>;
export function setValueAsyncCallback<CBValue>(
  ...cbArgs: [cbState?: RecoilState<CBValue>, cbNewValue?: CBValue]
): ReturnType<typeof setValueAsyncCallback> {
  const [cbState, cbNewValue] = cbArgs;
  return (cbInterface: CallbackInterface) =>
    partial(
      async <TValue extends CBValue>(
        state: RecoilState<TValue> = cbState as RecoilState<TValue & CBValue>,
        newValue: TValue = cbNewValue as TValue
      ) => setValueAsync(cbInterface, state, newValue),
      ...cbArgs
    );
}

export const setValueFamily = <TValue, TParam extends string>(
  cbInterface: CallbackInterface,
  state: RecoilStateFamily<TValue, TParam>,
  newValues: Record<TParam, TValue>
) => {
  for (const [key, newValue] of Object.entries(newValues) as [TParam, TValue][]) {
    setValue(cbInterface, state(key), newValue);
  }
};
export function setValueFamilyCallback<TValue, TParam extends string>(): SetValueCallback<
  Record<TParam, TValue>,
  [state: RecoilStateFamily<TValue, TParam>, newValues: Record<TParam, TValue>]
>;
export function setValueFamilyCallback<TValue, TParam extends string>(
  state: RecoilStateFamily<TValue, TParam>
): SetValueCallback<Record<TParam, TValue>, [newValues: Record<TParam, TValue>]>;
// export function setValueFamilyCallback<TValue, TParam extends string>(state: undefined, newValues: Record<TParam, TValue>): SetValueCallback<Record<TParam, TValue>, [state: RecoilStateFamily<TValue, TParam>]>;
export function setValueFamilyCallback<TValue, TParam extends string>(
  state: RecoilStateFamily<TValue, TParam>,
  newValues: Record<TParam, TValue>
): SetValueCallback<Record<TParam, TValue>, []>;
export function setValueFamilyCallback<CBValue, CBParam extends string>(
  ...cbArgs: [cbState?: RecoilStateFamily<CBValue, CBParam>, cbNewValues?: Record<CBParam, CBValue>]
): ReturnType<typeof setValueFamilyCallback> {
  const [cbState, cbNewValues] = cbArgs;
  return (cbInterface: CallbackInterface) =>
    partial(
      <TValue extends CBValue, TParam extends CBParam>(
        state: RecoilStateFamily<TValue, TParam> = cbState as RecoilStateFamily<TValue & CBValue, TParam>,
        newValues: Record<TParam, TValue> = cbNewValues as Record<TParam, TValue & CBValue>
      ) => setValueFamily(cbInterface, state, newValues),
      ...cbArgs
    );
}

export const setValueFamilyAsync = async <TValue, TParam extends string>(
  cbInterface: CallbackInterface,
  state: RecoilStateFamily<TValue, TParam>,
  newValues: Record<TParam, TValue>
) => {
  await Promise.all(
    (Object.entries(newValues) as [TParam, TValue][]).map(async ([key, newValue]) =>
      setValueAsync(cbInterface, state(key), newValue)
    )
  );
};
export function setValueFamilyAsyncCallback<TValue, TParam extends string>(): SetValueAsyncCallback<
  Record<TParam, TValue>,
  [state: RecoilStateFamily<TValue, TParam>, newValues: Record<TParam, TValue>]
>;
export function setValueFamilyAsyncCallback<TValue, TParam extends string>(
  state: RecoilStateFamily<TValue, TParam>
): SetValueAsyncCallback<Record<TParam, TValue>, [newValues: Record<TParam, TValue>]>;
// export function setValueFamilyAsyncCallback<TValue, TParam extends string>(state: undefined, newValues: Record<TParam, TValue>): SetValueAsyncCallback<Record<TParam, TValue>, [state: RecoilStateFamily<TValue, TParam>]>;
export function setValueFamilyAsyncCallback<TValue, TParam extends string>(
  state: RecoilStateFamily<TValue, TParam>,
  newValues: Record<TParam, TValue>
): SetValueAsyncCallback<Record<TParam, TValue>, []>;
export function setValueFamilyAsyncCallback<CBValue, CBParam extends string>(
  ...cbArgs: [cbState?: RecoilStateFamily<CBValue, CBParam>, cbNewValues?: Record<CBParam, CBValue>]
): ReturnType<typeof setValueFamilyAsyncCallback> {
  const [cbState, cbNewValues] = cbArgs;
  return (cbInterface: CallbackInterface) =>
    partial(
      async <TValue extends CBValue, TParam extends CBParam>(
        state: RecoilStateFamily<TValue, TParam> = cbState as RecoilStateFamily<TValue & CBValue, TParam>,
        newValues: Record<TParam, TValue> = cbNewValues as Record<TParam, TValue & CBValue>
      ) => setValueFamilyAsync(cbInterface, state, newValues),
      ...cbArgs
    );
}

export const setValues = <TValues extends RecoilValuesRecord>(
  cbInterface: CallbackInterface,
  states: ReadonlyMap<string, RecoilStateReference>,
  newValues: TValues
) => {
  for (const [key, newValue] of Object.entries(newValues).filter(([key]) => states.has(key))) {
    const { state, params } = states.get(key)!;
    if (!isNil(params)) {
      setValueFamily(cbInterface, state as RecoilStateFamily<unknown, string>, newValue as Record<string, unknown>);
    } else {
      setValue(cbInterface, state as RecoilState<unknown>, newValue);
    }
  }
};
export function setValuesCallback<TValues extends RecoilValuesRecord>(): SetValueCallback<
  TValues,
  [states: ReadonlyMap<string, RecoilStateReference>, newValues: TValues]
>;
export function setValuesCallback<TValues extends RecoilValuesRecord>(
  states: ReadonlyMap<string, RecoilStateReference>
): SetValueCallback<TValues, [newValues: TValues]>;
// export function setValuesCallback<TValues extends RecoilValuesRecord>(states: undefined, newValues: TValues): SetValueCallback<TValues, [states: ReadonlyMap<string, RecoilStateReference>]>;
export function setValuesCallback<TValues extends RecoilValuesRecord>(
  states: ReadonlyMap<string, RecoilStateReference>,
  newValues: TValues
): SetValueCallback<TValues, []>;
export function setValuesCallback<CBValues extends RecoilValuesRecord>(
  ...cbArgs: [cbStates?: ReadonlyMap<string, RecoilStateReference>, cbNewValues?: CBValues]
): ReturnType<typeof setValuesCallback> {
  const [cbStates, cbNewValues] = cbArgs;
  return (cbInterface: CallbackInterface) =>
    partial(
      <TValues extends CBValues>(
        states: ReadonlyMap<string, RecoilStateReference> = cbStates!,
        newValues: TValues = cbNewValues as TValues
      ) => setValues(cbInterface, states, newValues),
      ...cbArgs
    );
}

export const setValuesAsync = async <TValues extends RecoilValuesRecord>(
  cbInterface: CallbackInterface,
  states: ReadonlyMap<string, RecoilStateReference>,
  newValues: TValues
) => {
  await Promise.all(
    Object.entries(newValues)
      .filter(([key]) => states.has(key))
      .map(async ([key, newValue]) => {
        const { state, params } = states.get(key)!;
        if (!isNil(params)) {
          setValueFamilyAsync(
            cbInterface,
            state as RecoilStateFamily<unknown, string>,
            newValue as Record<string, unknown>
          );
        } else {
          setValueAsync(cbInterface, state as RecoilState<unknown>, newValue);
        }
      })
  );
};
export function setValuesAsyncCallback<TValues extends RecoilValuesRecord>(): SetValueAsyncCallback<
  TValues,
  [states: ReadonlyMap<string, RecoilStateReference>, newValues: TValues]
>;
export function setValuesAsyncCallback<TValues extends RecoilValuesRecord>(
  states: ReadonlyMap<string, RecoilStateReference>
): SetValueAsyncCallback<TValues, [newValues: TValues]>;
// export function setValuesAsyncCallback<TValues extends RecoilValuesRecord>(states: undefined, newValues: TValues): SetValueAsyncCallback<TValues, [states: ReadonlyMap<string, RecoilStateReference>]>;
export function setValuesAsyncCallback<TValues extends RecoilValuesRecord>(
  states: ReadonlyMap<string, RecoilStateReference>,
  newValues: TValues
): SetValueAsyncCallback<TValues, []>;
export function setValuesAsyncCallback<CBValues extends RecoilValuesRecord>(
  ...cbArgs: [cbStates?: ReadonlyMap<string, RecoilStateReference>, cbNewValues?: CBValues]
): ReturnType<typeof setValuesAsyncCallback> {
  const [cbStates, cbNewValues] = cbArgs;
  return (cbInterface: CallbackInterface) =>
    partial(
      async <TValues extends CBValues>(
        states: ReadonlyMap<string, RecoilStateReference> = cbStates!,
        newValues: TValues = cbNewValues as TValues
      ) => await setValuesAsync(cbInterface, states, newValues),
      ...cbArgs
    );
}
